import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, Paper, Stack, IconButton, Tooltip, Typography, Chip, Menu, MenuItem } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { io } from 'socket.io-client';
import { Mic as MicIcon, MicOff as MicOffIcon, Videocam as CamIcon, VideocamOff as CamOffIcon, ScreenShare as ShareIcon, StopScreenShare as StopShareIcon, CallEnd as CallEndIcon, Fullscreen as FullIcon, FullscreenExit as FullExitIcon, ViewModule as GridIcon, RecordVoiceOver as SpeakerIcon, PhotoCameraFront as CamSelectIcon } from '@mui/icons-material';

export default function CallWindow() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const conversationId = params.get('conv');

  const [inCall, setInCall] = useState(false);
  const [isMicOn, setIsMicOn] = useState(true);
  const [isCamOn, setIsCamOn] = useState(true);
  const [isSharing, setIsSharing] = useState(false);
  const [showSelfView, setShowSelfView] = useState(true);

  const socketRef = useRef(null);
  const mySocketIdRef = useRef(null);
  const localVideoRef = useRef(null);
  const containerRef = useRef(null);
  const localStreamRef = useRef(null);
  const pcsRef = useRef({}); // peerId -> RTCPeerConnection
  const [videoDevices, setVideoDevices] = useState([]);
  const [selectedCamId, setSelectedCamId] = useState('');
  const [camMenuAnchor, setCamMenuAnchor] = useState(null);
  const makingOfferRef = useRef({}); // peerId -> boolean
  const [remoteStreams, setRemoteStreams] = useState({}); // peerId -> MediaStream
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'speaker'
  const [activeSpeakerId, setActiveSpeakerId] = useState(null); // peerId | 'self'

  // WebAudio for active speaker detection
  const audioCtxRef = useRef(null);
  const analyserByPeerRef = useRef({}); // id -> { analyser, data, decay }
  const levelsRef = useRef({}); // id -> smoothed level
  const lastSwitchAtRef = useRef(0);

  const ICE_SERVERS = useMemo(() => ([{ urls: 'stun:stun.l.google.com:19302' }]), []);
  const SERVER_URL = useMemo(
    () => (process.env.REACT_APP_SERVER_URL || process.env.REACT_APP_API_BASE_URL || 'http://localhost:5002'),
    []
  );

  const attachLocalStream = async () => {
    if (localStreamRef.current) return localStreamRef.current;
    const constraints = {
      audio: true,
      video: selectedCamId ? { deviceId: { exact: selectedCamId } } : true,
    };
    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    localStreamRef.current = stream;
    if (localVideoRef.current) {
      localVideoRef.current.srcObject = stream;
      localVideoRef.current.muted = true;
      await localVideoRef.current.play().catch(() => {});
    }
    // After permission, enumerate devices
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const vids = devices.filter(d => d.kind === 'videoinput');
      setVideoDevices(vids);
      // If we don't have a selected ID, prefer the label-matching track or first device
      if (!selectedCamId) {
        const vTrack = stream.getVideoTracks()[0];
        const byLabel = vids.find(d => vTrack && d.label && vTrack.label && d.label.toLowerCase() === vTrack.label.toLowerCase());
        setSelectedCamId(byLabel?.deviceId || vids[0]?.deviceId || '');
      }
    } catch {}
    // Setup analyser for self
    try {
      if (!audioCtxRef.current) audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const ctx = audioCtxRef.current;
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      src.connect(analyser);
      analyserByPeerRef.current['self'] = { analyser, data: new Uint8Array(analyser.frequencyBinCount), decay: 0.92 };
      levelsRef.current['self'] = 0;
    } catch {}
    return stream;
  };

  const toggleFullscreen = async () => {
    try {
      const el = containerRef.current || document.documentElement;
      if (!document.fullscreenElement) {
        await el.requestFullscreen?.();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen?.();
        setIsFullscreen(false);
      }
    } catch {}
  };

  const createPeerConnection = (peerId) => {
    if (pcsRef.current[peerId]) return pcsRef.current[peerId];
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
    // Pre-create transceivers to stabilize m-line order
    try {
      pc.addTransceiver('audio', { direction: 'sendrecv' });
      pc.addTransceiver('video', { direction: 'sendrecv' });
    } catch {}
    pc.onicecandidate = (e) => {
      if (e.candidate && socketRef.current) {
        socketRef.current.emit('rtc:ice', { to: peerId, candidate: e.candidate });
      }
    };
    pc.ontrack = (e) => {
      const [stream] = e.streams;
      setRemoteStreams(prev => ({ ...prev, [peerId]: stream }));
    };
    pc.__localTracksAdded = false;
    pcsRef.current[peerId] = pc;
    return pc;
  };

  const ensureLocalTracksAdded = (pc) => {
    try {
      if (!pc || pc.__localTracksAdded) return;
      const stream = localStreamRef.current;
      if (!stream) return;
      const senders = (pc.getSenders && pc.getSenders()) || [];
      const aTrack = stream.getAudioTracks()[0] || null;
      const vTrack = stream.getVideoTracks()[0] || null;
      const aSender = senders.find(s => (s.track && s.track.kind === 'audio')) || senders.find(s => s.receiver && s.receiver.track && s.receiver.track.kind === 'audio');
      const vSender = senders.find(s => (s.track && s.track.kind === 'video')) || senders.find(s => s.receiver && s.receiver.track && s.receiver.track.kind === 'video');
      if (aSender && aTrack) { try { aSender.replaceTrack(aTrack); } catch {} }
      if (vSender && vTrack) { try { vSender.replaceTrack(vTrack); } catch {} }
      pc.__localTracksAdded = true;
    } catch {}
  };

  const cleanupPeer = (peerId) => {
    const pc = pcsRef.current[peerId];
    if (pc) {
      try { pc.onicecandidate = null; pc.ontrack = null; pc.close(); } catch {}
      delete pcsRef.current[peerId];
    }
    setRemoteStreams(prev => { const next = { ...prev }; delete next[peerId]; return next; });
    // Remove analyser for that peer
    if (analyserByPeerRef.current[peerId]) {
      delete analyserByPeerRef.current[peerId];
      delete levelsRef.current[peerId];
    }
  };

  const leaveCall = () => {
    setInCall(false);
    Object.keys(pcsRef.current).forEach(pid => cleanupPeer(pid));
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(t => t.stop());
      localStreamRef.current = null;
    }
    try { socketRef.current?.disconnect(); } catch {}
    socketRef.current = null;
    setIsSharing(false);
    // close window shortly after to return user to app
    setTimeout(() => window.close(), 150);
  };

  useEffect(() => {
    // Keyboard shortcuts: m (mute), v (video), s (share), esc (leave)
    const keyHandler = (e) => {
      if (e.altKey || e.metaKey || e.ctrlKey) return;
      const k = e.key.toLowerCase();
      if (k === 'm') { e.preventDefault(); toggleMic(); }
      if (k === 'v') { e.preventDefault(); toggleCam(); }
      if (k === 's') { e.preventDefault(); shareScreen(); }
      if (k === 'f') { e.preventDefault(); toggleFullscreen(); }
      if (k === 'escape') { e.preventDefault(); leaveCall(); }
    };
    window.addEventListener('keydown', keyHandler);
    if (!conversationId) { navigate('/team'); return; }
    (async () => {
      try {
        const stream = await attachLocalStream();
        stream.getAudioTracks().forEach(t => (t.enabled = isMicOn));
        stream.getVideoTracks().forEach(t => (t.enabled = isCamOn));

        const s = io(`${SERVER_URL}/rtc`, { transports: ['websocket'], withCredentials: true });
        socketRef.current = s;
        s.on('connect', () => {
          mySocketIdRef.current = s.id;
          s.emit('rtc:join', { room: conversationId, userId: 'me' });
        });

        s.on('rtc:peers', async ({ peers }) => {
          const others = (peers || []).filter(pid => pid && pid !== mySocketIdRef.current);
          for (const pid of others) {
            const pc = createPeerConnection(pid);
            ensureLocalTracksAdded(pc);
            if (pc.signalingState === 'stable') {
              makingOfferRef.current[pid] = true;
              try {
                const offer = await pc.createOffer();
                await pc.setLocalDescription(offer);
                s.emit('rtc:offer', { to: pid, sdp: offer });
              } finally {
                makingOfferRef.current[pid] = false;
              }
            }
          }
        });

        s.on('rtc:offer', async ({ from, sdp }) => {
          if (!from || from === mySocketIdRef.current) return; // ignore self
          const pc = createPeerConnection(from);
          ensureLocalTracksAdded(pc);
          const offer = new RTCSessionDescription(sdp);
          if (offer.type !== 'offer') return;
          const collision = makingOfferRef.current[from] || pc.signalingState !== 'stable';
          if (collision) {
            try { await pc.setLocalDescription({ type: 'rollback' }); } catch {}
          }
          await pc.setRemoteDescription(offer);
          if (pc.signalingState !== 'have-remote-offer') return;
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          s.emit('rtc:answer', { to: from, sdp: answer });
        });

        s.on('rtc:answer', async ({ from, sdp }) => {
          const pc = pcsRef.current[from];
          if (!pc) return;
          if (pc.signalingState !== 'have-local-offer') return;
          await pc.setRemoteDescription(new RTCSessionDescription(sdp));
        });

        s.on('rtc:ice', async ({ from, candidate }) => {
          const pc = pcsRef.current[from];
          if (!pc) return;
          try { await pc.addIceCandidate(new RTCIceCandidate(candidate)); } catch {}
        });

        s.on('rtc:user-left', ({ socketId }) => { cleanupPeer(socketId); });
        s.on('disconnect', () => { leaveCall(); });
        setInCall(true);
      } catch (e) {
        // if media blocked, close
        setTimeout(() => window.close(), 200);
      }
    })();

    const beforeUnload = () => { try { leaveCall(); } catch {} };
    window.addEventListener('beforeunload', beforeUnload);
    return () => { window.removeEventListener('beforeunload', beforeUnload); window.removeEventListener('keydown', keyHandler); };
  }, [conversationId, SERVER_URL]);

  const toggleMic = () => {
    const next = !isMicOn;
    setIsMicOn(next);
    if (localStreamRef.current) localStreamRef.current.getAudioTracks().forEach(t => (t.enabled = next));
  };
  const toggleCam = () => {
    const next = !isCamOn;
    setIsCamOn(next);
    if (localStreamRef.current) localStreamRef.current.getVideoTracks().forEach(t => (t.enabled = next));
  };
  const shareScreen = async () => {
    try {
      if (!inCall) return;
      if (!isSharing) {
        const display = await navigator.mediaDevices.getDisplayMedia({ video: true });
        const screenTrack = display.getVideoTracks()[0];
        Object.values(pcsRef.current).forEach(pc => {
          const sender = pc.getSenders().find(s => s.track && s.track.kind === 'video');
          if (sender) sender.replaceTrack(screenTrack);
        });
        setIsSharing(true);
        screenTrack.onended = () => {
          if (localStreamRef.current) {
            const camTrack = localStreamRef.current.getVideoTracks()[0];
            Object.values(pcsRef.current).forEach(pc => {
              const sender = pc.getSenders().find(s => s.track && s.track.kind === 'video');
              if (sender && camTrack) sender.replaceTrack(camTrack);
            });
          }
          setIsSharing(false);
        };
      }
    } catch {}
  };

  const participantsCount = Object.keys(remoteStreams).length + (inCall ? 1 : 0);

  const VideoTile = ({ stream, label }) => (
    <Box sx={{ position: 'relative', width: '100%', aspectRatio: '16 / 9', bgcolor: '#000', borderRadius: 1.25, overflow: 'hidden', border: (t) => `1px solid ${alpha(t.palette.divider, 0.5)}`, boxShadow: (t) => t.shadows[2] }}>
      <video
        ref={(el) => { if (el && stream) { el.srcObject = stream; el.autoplay = true; el.playsInline = true; el.muted = false; el.style.width = '100%'; el.style.height = '100%'; el.style.objectFit = 'cover'; el.play().catch(() => {}); } }}
      />
      <Box sx={{ position: 'absolute', left: 8, bottom: 8, bgcolor: (t) => alpha(t.palette.common.black, 0.5), color: '#fff', px: 1, py: 0.25, borderRadius: 1, fontSize: '0.75rem', letterSpacing: 0.2 }}>
        {label}
      </Box>
    </Box>
  );

  // Setup/maintain analyser for remote streams when they arrive
  useEffect(() => {
    if (!audioCtxRef.current) {
      try { audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)(); } catch {}
    }
    const ctx = audioCtxRef.current;
    if (!ctx) return;
    Object.entries(remoteStreams).forEach(([pid, stream]) => {
      if (!analyserByPeerRef.current[pid] && stream) {
        try {
          const src = ctx.createMediaStreamSource(stream);
          const analyser = ctx.createAnalyser();
          analyser.fftSize = 512;
          src.connect(analyser);
          analyserByPeerRef.current[pid] = { analyser, data: new Uint8Array(analyser.frequencyBinCount), decay: 0.92 };
          levelsRef.current[pid] = 0;
        } catch {}
      }
    });
    // Clean up analysers for removed streams
    Object.keys(analyserByPeerRef.current).forEach((id) => {
      if (id !== 'self' && !remoteStreams[id]) {
        delete analyserByPeerRef.current[id];
        delete levelsRef.current[id];
      }
    });
  }, [remoteStreams]);

  // Active speaker detection loop
  useEffect(() => {
    let raf = null;
    const HYSTERESIS = 1.2;
    const MIN_SWITCH_MS = 600;
    const THRESHOLD = 0.05; // normalized 0..1
    const tick = () => {
      const now = Date.now();
      let topId = activeSpeakerId || 'self';
      let topVal = 0;
      const entries = Object.entries(analyserByPeerRef.current);
      for (const [id, obj] of entries) {
        const { analyser, data, decay } = obj;
        if (!analyser) continue;
        analyser.getByteTimeDomainData(data);
        // Compute RMS
        let sum = 0;
        for (let i = 0; i < data.length; i++) {
          const v = (data[i] - 128) / 128; // -1..1
          sum += v * v;
        }
        const rms = Math.sqrt(sum / data.length);
        const prev = levelsRef.current[id] || 0;
        const level = Math.max(rms, prev * decay);
        levelsRef.current[id] = level;
      }
      // Decide active speaker
      for (const [id, lvl] of Object.entries(levelsRef.current)) {
        if (lvl > topVal) { topVal = lvl; topId = id; }
      }
      const current = activeSpeakerId || 'self';
      const currentLevel = levelsRef.current[current] || 0;
      const challengerLevel = levelsRef.current[topId] || 0;
      const allowSwitch = (now - lastSwitchAtRef.current) > MIN_SWITCH_MS;
      const challengerBeats = challengerLevel > Math.max(THRESHOLD, currentLevel * HYSTERESIS);
      if (allowSwitch && challengerBeats && current !== topId) {
        setActiveSpeakerId(topId);
        lastSwitchAtRef.current = now;
      } else if (!activeSpeakerId) {
        // initialize once
        setActiveSpeakerId(topId);
        lastSwitchAtRef.current = now;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => { if (raf) cancelAnimationFrame(raf); };
  }, [activeSpeakerId]);

  // Handle camera device change: get new stream and replace outgoing video tracks
  useEffect(() => {
    const switchCamera = async () => {
      if (!selectedCamId) return;
      try {
        const current = localStreamRef.current;
        const newStream = await navigator.mediaDevices.getUserMedia({
          audio: current ? { deviceId: current.getAudioTracks()[0]?.getSettings?.().deviceId } : true,
          video: { deviceId: { exact: selectedCamId } },
        });
        // Update preview
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = newStream;
          await localVideoRef.current.play().catch(() => {});
        }
        // Replace video track in all peer connections
        const newVTrack = newStream.getVideoTracks()[0];
        Object.values(pcsRef.current).forEach(pc => {
          const sender = pc.getSenders().find(s => s.track && s.track.kind === 'video');
          if (sender && newVTrack) {
            try { sender.replaceTrack(newVTrack); } catch {}
          }
        });
        // Stop previous video tracks to release hardware
        if (current) current.getVideoTracks().forEach(t => { try { t.stop(); } catch {} });
        // Keep using the same audio track
        if (current) newStream.getAudioTracks().forEach(t => { try { t.stop(); } catch {} });
        // Merge tracks: keep existing audio, swap video
        const merged = new MediaStream();
        current?.getAudioTracks().forEach(t => merged.addTrack(t));
        if (newVTrack) merged.addTrack(newVTrack);
        localStreamRef.current = merged;
      } catch (e) {
        console.warn('Failed to switch camera', e);
      }
    };
    if (inCall || localStreamRef.current) {
      switchCamera();
    }
  }, [selectedCamId]);

  return (
    <Box ref={containerRef} sx={{ height: '100vh', bgcolor: (t) => t.palette.background.default, p: 1.5 }}>
      <Paper elevation={2} sx={{
        px: 1.5, py: 1,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5,
        border: (t) => `1px solid ${alpha(t.palette.divider, 0.6)}`,
        borderRadius: 2,
        backdropFilter: 'blur(6px)'
      }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Typography variant="subtitle2" sx={{ letterSpacing: 0.4, fontWeight: 600 }}>Video Call</Typography>
          <Chip size="small" label={inCall ? `In Call • ${participantsCount}` : 'Idle'} color={inCall ? 'success' : 'default'} />
        </Stack>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Tooltip title={viewMode === 'speaker' ? 'Speaker view' : 'Grid view'}>
            <IconButton size="small" onClick={() => setViewMode(v => v === 'speaker' ? 'grid' : 'speaker')}>
              {viewMode === 'speaker' ? <SpeakerIcon fontSize="small" /> : <GridIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Tooltip title={isMicOn ? 'Mute (M)' : 'Unmute (M)'}>
            <span>
              <IconButton size="small" onClick={toggleMic} disabled={!inCall}>
                {isMicOn ? <MicIcon fontSize="small" /> : <MicOffIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={videoDevices.length ? 'Select camera' : 'Select camera (grant permission first)'}>
            <span>
              <IconButton size="small" onClick={(e) => setCamMenuAnchor(e.currentTarget)} disabled={!inCall && !videoDevices.length}>
                <CamSelectIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Menu anchorEl={camMenuAnchor} open={Boolean(camMenuAnchor)} onClose={() => setCamMenuAnchor(null)}>
            {videoDevices.length === 0 ? (
              <MenuItem disabled>No cameras found</MenuItem>
            ) : (
              videoDevices.map((d) => (
                <MenuItem key={d.deviceId} selected={d.deviceId === selectedCamId} onClick={() => { setSelectedCamId(d.deviceId); setCamMenuAnchor(null); }}>
                  {d.label || `Camera ${d.deviceId.slice(-4)}`}
                </MenuItem>
              ))
            )}
          </Menu>
          <Tooltip title={isCamOn ? 'Turn camera off (V)' : 'Turn camera on (V)'}>
            <span>
              <IconButton size="small" onClick={toggleCam} disabled={!inCall}>
                {isCamOn ? <CamIcon fontSize="small" /> : <CamOffIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={isSharing ? 'Sharing...' : 'Share screen (S)'}>
            <span>
              <IconButton size="small" onClick={shareScreen} disabled={!inCall || isSharing}>
                {isSharing ? <StopShareIcon fontSize="small" /> : <ShareIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={isFullscreen ? 'Exit fullscreen (F)' : 'Enter fullscreen (F)'}>
            <IconButton size="small" onClick={toggleFullscreen}>
              {isFullscreen ? <FullExitIcon fontSize="small" /> : <FullIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Leave (Esc)">
            <IconButton size="small" color="error" onClick={leaveCall}>
              <CallEndIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Paper>
      <Box sx={{ p: 1 }}>
        {viewMode === 'grid' ? (
          <>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: 1.25
            }}>
              {Object.entries(remoteStreams).map(([pid, stream]) => (
                <VideoTile key={pid} stream={stream} label={`Participant ${pid.slice(-4)}`} />
              ))}
            </Box>
            {showSelfView && localStreamRef.current ? (
              <Box sx={{ mt: 1.5, display: 'inline-block' }}>
                <Box sx={{ position: 'relative', width: 280, aspectRatio: '16 / 9', bgcolor: '#000', borderRadius: 1.25, overflow: 'hidden', border: (t) => `1px solid ${alpha(t.palette.divider, 0.5)}`, boxShadow: (t) => t.shadows[4] }}>
                  <video ref={localVideoRef} onDoubleClick={toggleFullscreen} style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }} autoPlay playsInline muted />
                  <Box sx={{ position: 'absolute', left: 8, bottom: 8, bgcolor: (t) => alpha(t.palette.common.black, 0.5), color: '#fff', px: 1, py: 0.25, borderRadius: 1, fontSize: '0.75rem' }}>You</Box>
                </Box>
              </Box>
            ) : null}
          </>
        ) : (
          // Speaker view
          <>
            <Box sx={{ mb: 1.25 }}>
              {(() => {
                let mainId = activeSpeakerId || 'self';
                let mainLabel = mainId === 'self' ? 'You' : `Participant ${String(mainId).slice(-4)}`;
                let stream = mainId === 'self' ? (localStreamRef.current || null) : remoteStreams[mainId];
                if (!stream) {
                  // fallback: pick first remote or self
                  const first = Object.keys(remoteStreams)[0];
                  mainId = first || 'self';
                  mainLabel = mainId === 'self' ? 'You' : `Participant ${String(mainId).slice(-4)}`;
                  stream = mainId === 'self' ? (localStreamRef.current || null) : remoteStreams[mainId];
                }
                return (
                  <Box sx={{ position: 'relative', width: '100%', aspectRatio: '16 / 9', bgcolor: '#000', borderRadius: 1.5, overflow: 'hidden', border: (t) => `1px solid ${alpha(t.palette.divider, 0.5)}`, boxShadow: (t) => t.shadows[4] }}>
                    <video ref={(el) => { if (el && stream) { el.srcObject = stream; el.autoplay = true; el.playsInline = true; el.muted = (mainId === 'self'); el.style.width = '100%'; el.style.height = '100%'; el.style.objectFit = 'cover'; el.play().catch(() => {}); } }} onDoubleClick={toggleFullscreen} style={{ cursor: 'pointer' }} />
                    <Box sx={{ position: 'absolute', left: 12, bottom: 12, bgcolor: (t) => alpha(t.palette.common.black, 0.5), color: '#fff', px: 1.25, py: 0.5, borderRadius: 1, fontSize: '0.85rem', letterSpacing: 0.2 }}>{mainLabel}</Box>
                  </Box>
                );
              })()}
            </Box>
            {/* Thumbnails */}
            <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 1 }}>
              {/* Self thumbnail if not active */}
              {localStreamRef.current && activeSpeakerId !== 'self' && (
                <Box sx={{ position: 'relative', width: 160, aspectRatio: '16 / 9', bgcolor: '#000', borderRadius: 1, overflow: 'hidden', border: (t) => `1px solid ${alpha(t.palette.divider, 0.4)}` }}>
                  <video ref={(el) => { if (el && localStreamRef.current) { el.srcObject = localStreamRef.current; el.autoplay = true; el.playsInline = true; el.muted = true; el.style.width = '100%'; el.style.height = '100%'; el.style.objectFit = 'cover'; el.play().catch(() => {}); } }} />
                  <Box sx={{ position: 'absolute', left: 8, bottom: 8, bgcolor: (t) => alpha(t.palette.common.black, 0.45), color: '#fff', px: 0.75, py: 0.25, borderRadius: 1, fontSize: '0.7rem' }}>You</Box>
                </Box>
              )}
              {Object.entries(remoteStreams).map(([pid, stream]) => (
                activeSpeakerId === pid ? null : (
                  <Box key={pid} sx={{ position: 'relative', width: 160, aspectRatio: '16 / 9', bgcolor: '#000', borderRadius: 1, overflow: 'hidden', border: (t) => `1px solid ${alpha(t.palette.divider, 0.4)}` }}>
                    <video ref={(el) => { if (el && stream) { el.srcObject = stream; el.autoplay = true; el.playsInline = true; el.muted = false; el.style.width = '100%'; el.style.height = '100%'; el.style.objectFit = 'cover'; el.play().catch(() => {}); } }} />
                    <Box sx={{ position: 'absolute', left: 8, bottom: 8, bgcolor: (t) => alpha(t.palette.common.black, 0.45), color: '#fff', px: 0.75, py: 0.25, borderRadius: 1, fontSize: '0.7rem' }}>{`Participant ${pid.slice(-4)}`}</Box>
                  </Box>
                )
              ))}
            </Stack>
          </>
        )}
      </Box>
    </Box>
  );
}
